import { useEffect, useRef } from 'react';

export default function HowItWorks() {
  const ref = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = [ref.current, projectsRef.current].filter(Boolean) as HTMLDivElement[];
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.1 }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const steps = [
    {
      num: '01',
      title: 'Pressurized Injection',
      desc: '4,061 PSI pump injects polyvinyl into drilled points near infiltration zones.',
      icon: 'ri-tools-fill',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist10.png',
    },
    {
      num: '02',
      title: 'Polyvinyl Technology',
      desc: 'Travels opposite to water, sealing from the source. Reaches deep micro-fissures invisible to the eye.',
      icon: 'ri-drop-fill',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist11.png',
    },
    {
      num: '03',
      title: 'Permanent Seal',
      desc: 'Hardens like rubber. Blocks CO\u2082 and chlorides. Immediate, lasting result with 5-year warranty.',
      icon: 'ri-shield-check-fill',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist12.png',
    },
  ];

  const locations = [
    { icon: 'ri-car-fill', label: 'Parking Garages' },
    { icon: 'ri-road-map-fill', label: 'Tunnels' },
    { icon: 'ri-building-4-fill', label: 'High-Rise Buildings' },
    { icon: 'ri-arrow-up-down-fill', label: 'Elevator Pits' },
    { icon: 'ri-layout-top-fill', label: 'Podium Slabs' },
    { icon: 'ri-water-flash-fill', label: 'Swimming Pools' },
    { icon: 'ri-window-fill', label: 'Window Frames' },
    { icon: 'ri-anchor-fill', label: 'Sea Walls' },
    { icon: 'ri-expand-left-right-fill', label: 'Expansion Joints' },
    { icon: 'ri-home-4-fill', label: 'Basements' },
  ];

  const projects = [
    {
      name: '830 Brickell',
      city: 'Miami, FL',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist10.png',
    },
    {
      name: 'Seminole Hard Rock Hotel & Casino',
      city: 'Hollywood, FL',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist11.png',
    },
    {
      name: 'Fisher Island',
      city: 'Miami, FL',
      img: 'https://clwizard.com/wp-content/uploads/2022/06/Blushot-Concrete-Leak-Specialist12.png',
    },
    {
      name: 'Yacht Club & Portofino',
      city: 'Miami Beach',
      img: 'https://clwizard.com/wp-content/uploads/2025/07/Yacht-Club-Portofino-Water-Intrusion-Repaired-1.jpg',
    },
    {
      name: 'Sands Pointe Condos',
      city: 'Sunny Isles Beach',
      img: 'https://clwizard.com/wp-content/uploads/2025/07/Miami-Beach-Apartment-Water-Intrustion-Repair.jpg',
    },
  ];

  return (
    <>
      {/* How It Works — light background */}
      <section id="how-it-works" className="bg-[#f4f6f9] py-20 md:py-28">
        <div
          ref={ref}
          className="max-w-6xl mx-auto px-4 md:px-8 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          <div className="text-center mb-16">
            <span className="inline-block bg-[#0055ff]/10 border border-[#0055ff]/30 text-[#0055ff] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              The Process
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#0a0e1a]">How It Works</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {steps.map((s) => (
              <div key={s.num} className="group bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#0055ff]/40 transition-all duration-200">
                <div className="relative overflow-hidden h-52">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-[#0055ff] text-white font-black text-xl w-11 h-11 flex items-center justify-center rounded-md">
                    {s.num}
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-8 h-8 flex items-center justify-center text-[#0055ff] text-xl">
                      <i className={s.icon}></i>
                    </span>
                    <h3 className="text-[#0a0e1a] font-bold text-lg">{s.title}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Where we work */}
          <div className="border-t border-gray-200 pt-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-black text-[#0a0e1a] mb-2">Where We Work</h2>
              <p className="text-gray-500 text-sm">Every structure. Every challenge.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
              {locations.map((l) => (
                <div
                  key={l.label}
                  className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-lg p-3 md:p-4 hover:border-[#0055ff]/40 hover:bg-[#0055ff]/5 transition-all duration-200 cursor-default"
                >
                  <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center text-[#0055ff] text-xl md:text-2xl">
                    <i className={l.icon}></i>
                  </span>
                  <span className="text-[#0a0e1a] text-xs font-semibold text-center leading-tight">{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recently Completed Projects — dark background */}
      <section id="projects" className="bg-[#0a0e1a] py-20 md:py-28">
        <div
          ref={projectsRef}
          className="max-w-6xl mx-auto px-4 md:px-8 opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          <div className="text-center mb-14">
            <span className="inline-block bg-[#0055ff]/10 border border-[#0055ff]/30 text-[#4d8fff] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Portfolio
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white">Recently Completed Projects</h2>
            <p className="text-gray-400 text-base mt-3">Trusted by South Florida&apos;s most demanding structures.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.slice(0, 3).map((p) => (
              <div key={p.name} className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-[#0055ff]/40 transition-all duration-300">
                <div className="w-full h-52 md:h-56">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a]/90 via-[#0a0e1a]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <h3 className="text-white font-bold text-sm md:text-base leading-tight">{p.name}</h3>
                  <p className="text-[#4d8fff] text-xs font-semibold mt-1 flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-fill"></i></span>
                    {p.city}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 lg:max-w-2xl lg:mx-auto">
            {projects.slice(3).map((p) => (
              <div key={p.name} className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-[#0055ff]/40 transition-all duration-300">
                <div className="w-full h-52 md:h-56">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a]/90 via-[#0a0e1a]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
                  <h3 className="text-white font-bold text-sm md:text-base leading-tight">{p.name}</h3>
                  <p className="text-[#4d8fff] text-xs font-semibold mt-1 flex items-center gap-1">
                    <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-fill"></i></span>
                    {p.city}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
